import { usePost } from "@albertine/api-client";
import { MemberSourceId, MemberSourceIdMap } from "@albertine/types";
import type { MembershipTier, Tenant } from "@albertine/types/openapi/lmt";
import { zodResolver } from "@hookform/resolvers/zod";
import { AgentPicker } from "@prototypes/agent-picker/AgentPicker";
import { toAssignableAgents } from "@prototypes/agent-picker/assignableAgents";
import { FormField, FormInput } from "@prototypes/form-field/FormField";
import { OwnedField } from "@prototypes/member-profile/overlays/FormFields";
import { TitleField } from "@prototypes/member-profile/overlays/TitleField";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import PhoneInput from "react-phone-number-input/input";
import { useHistory } from "react-router";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useAgents } from "../../common/hooks/useAgents";
import { invalidateListAllMembers } from "../../common/hooks/useMembers";
import { EventEmitter } from "../../EventEmitter";
import { NewMemberMembershipFields } from "./NewMemberMembershipFields";
import {
    type ContactDetailsInUse,
    type NewMemberFormInput,
    type NewMemberFormOutput,
    newMemberFormDefaults,
    newMemberFormSchema,
    noContactDetailsInUse,
    toCreateMemberRequest,
} from "./newMember.util";
import { useContactDetailsCheck } from "./useContactDetailsCheck";

const sourceOptions = MemberSourceId.options.map((source) => ({
    id: source,
    label: MemberSourceIdMap[source],
}));

type Props = {
    defaultTenant: Tenant;
    tenantOptions: Tenant[];
    membershipTiers: MembershipTier[];
    defaultAssignedTo: string | undefined;
    onClose: () => void;
};

export function NewMemberForm({
    defaultTenant,
    tenantOptions,
    membershipTiers,
    defaultAssignedTo,
    onClose,
}: Props) {
    const history = useHistory();
    const agents = useAgents();

    const [inUse, setInUse] = useState<ContactDetailsInUse>(noContactDetailsInUse);
    const schema = newMemberFormSchema(inUse);
    const form = useForm<NewMemberFormInput, unknown, NewMemberFormOutput>({
        resolver: zodResolver(schema),
        defaultValues: newMemberFormDefaults(
            defaultTenant,
            membershipTiers,
            defaultAssignedTo,
        ),
        mode: "onChange",
    });
    const { control, handleSubmit } = form;
    useContactDetailsCheck(form, setInUse);

    const values = useWatch({ control });
    const validation = schema.safeParse(values);
    const blockedReason = validation.success
        ? undefined
        : (validation.error.issues[0]?.message ?? "Check the form fields");

    const { mutate: createMember, isPending } = usePost("agentCreateMember", {
        ...invalidateListAllMembers,
        onSuccess: (response) => {
            EventEmitter.emit({
                name: "showToastMessage",
                params: { message: "Member added successfully", type: "info" },
            });
            onClose();
            history.push(`/member/${response.id}`);
        },
        onError: (error) => {
            window.alert(error.error);
        },
    });

    return (
        <form
            className="contents"
            noValidate
            onSubmit={handleSubmit((member) =>
                createMember({ json: toCreateMemberRequest(member) }),
            )}
        >
            <div className="flex flex-col gap-4">
                <div className="grid items-start gap-4 sm:grid-cols-3">
                    <Controller
                        control={control}
                        name="title"
                        render={({ field }) => (
                            <TitleField
                                id="new-member-title"
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                    <OwnedField
                        id="new-member-first-name"
                        label="First name"
                        kind="text"
                        control={control}
                        name="firstName"
                        autoComplete="off"
                        autoFocus
                    />
                    <OwnedField
                        id="new-member-last-name"
                        label="Last name"
                        kind="text"
                        control={control}
                        name="lastName"
                        autoComplete="off"
                    />
                </div>

                <div className="grid items-start gap-4 sm:grid-cols-2">
                    <Controller
                        control={control}
                        name="phoneNumber"
                        render={({ field, fieldState }) => (
                            <FormField
                                label="Phone number"
                                htmlFor="new-member-phone-number"
                                error={fieldState.error?.message}
                            >
                                <PhoneInput
                                    id="new-member-phone-number"
                                    inputComponent={FormInput}
                                    autoComplete="off"
                                    placeholder="+44 7878 123456"
                                    aria-invalid={fieldState.invalid || undefined}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(phoneNumber) =>
                                        field.onChange(phoneNumber ?? "")
                                    }
                                />
                            </FormField>
                        )}
                    />
                    <OwnedField
                        id="new-member-email"
                        label="Email address"
                        kind="text"
                        control={control}
                        name="email"
                        placeholder="example@example.com"
                        inputMode="email"
                        autoComplete="off"
                    />
                </div>

                <div className="grid items-start gap-4 sm:grid-cols-2">
                    <OwnedField
                        id="new-member-source"
                        label="Source"
                        kind="select"
                        options={sourceOptions}
                        control={control}
                        name="source"
                    />
                    <OwnedField
                        id="new-member-source-info"
                        label="Additional information on source"
                        kind="text"
                        control={control}
                        name="additionalNoteOnSource"
                        optional
                    />
                </div>

                <div className="grid items-start gap-4 sm:grid-cols-2">
                    <Controller
                        control={control}
                        name="assignedAgentId"
                        render={({ field, fieldState }) => (
                            <FormField
                                label="Assigned to"
                                htmlFor="new-member-assigned-to"
                                error={fieldState.error?.message}
                            >
                                <AgentPicker
                                    id="new-member-assigned-to"
                                    items={toAssignableAgents(
                                        agents,
                                        defaultAssignedTo,
                                    )}
                                    value={field.value}
                                    onChange={(agent) => field.onChange(agent.id)}
                                    positionerClassName="z-[600]"
                                />
                            </FormField>
                        )}
                    />
                </div>

                <Separator className="mt-2" />

                <NewMemberMembershipFields
                    form={form}
                    tenantOptions={tenantOptions}
                    membershipTiers={membershipTiers}
                />
            </div>

            <DialogFooter>
                <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isPending}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={!!blockedReason || isPending}
                    disabledReason={blockedReason}
                >
                    Create new member
                </Button>
            </DialogFooter>
        </form>
    );
}
